import React, { useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import Tabs from 'components/Tabs'
import Search from 'components/Search'
import ResourceTable from 'pages/Resources/ResourceTable'
import ProductLiteratureSearch from 'pages/shared/ProductLiterature'
import FieldTechHelp from 'pages/shared/FieldTechHelp'

import useAutoFocus from 'hooks/useAutoFocus'
import useFthVideos from 'hooks/useFthVideos'
import useResources from 'hooks/useResources'
import useProductLiterature from 'hooks/useProductLiterature'

import Resource from 'types/Resource'

import { PageHeader, PageTitle, screenSize } from 'styles/shared'

const TABS = ['field-tech-help', 'product-literature', 'videos', 'articles', 'faqs', 'thermostat-releases']

const Resources = () => {
  const navigate = useNavigate()
  const { tab } = useParams<{ tab?: string }>()

  const currentTab = tab && TABS.includes(tab) ? tab : TABS[0]
  const activeTabIndex = TABS.indexOf(currentTab)

  const { articles, faqs, videos, thermostatReleases, areResourcesLoaded } = useResources({ publishedOnly: true })

  const [displayFiltered, setDisplayFiltered] = useState(false)
  const [filteredVideos, setFilteredVideos] = useState<Array<Resource>>([])
  const [filteredArticles, setFilteredArticles] = useState<Array<Resource>>([])
  const [filteredFaqs, setFilteredFaqs] = useState<Array<Resource>>([])
  const [filteredThermostatReleases, setFilteredThermostatReleases] = useState<Array<Resource>>([])
  const [searchValue, setSearchValue] = useState('')
  const [submittedSearchValue, setSubmittedSearchValue] = useState('')
  const [showSpinner, setShowSpinner] = useState(false)
  const { fthVideos, isFthLoading } = useFthVideos(submittedSearchValue)
  const { productLiterature, isProductLiteratureLoaded, serverError: plServerError } = useProductLiterature(submittedSearchValue)
  const title = useRef(null)
  useAutoFocus({ element: title })

  const onSearchClicked = () => {
    if (searchValue.length > 0) {
      setShowSpinner(true)
      const videoResources: Array<Resource> = []
      const articleResources: Array<Resource> = []
      const faqsResources: Array<Resource> = []
      const thermostatReleaseResources: Array<Resource> = []
      videos.forEach((video) => {
        if (video.name.toLowerCase().includes(searchValue.toLowerCase())) {
          videoResources.push(video)
        }
      })
      articles.forEach((resource) => {
        if (resource.name.toLowerCase().includes(searchValue.toLowerCase())) {
          articleResources.push(resource)
        }
      })
      faqs.forEach((faq) => {
        if (faq.name.toLowerCase().includes(searchValue.toLowerCase())) {
          faqsResources.push(faq)
        }
      })

      thermostatReleases.forEach((release) => {
        if (release.version && (release.version.toLowerCase().includes(searchValue.toLowerCase()) || release.description.toLowerCase().includes(searchValue.toLowerCase()))) {
          thermostatReleaseResources.push(release)
        }
      })

      setFilteredVideos(videoResources)
      setFilteredArticles(articleResources)
      setFilteredFaqs(faqsResources)
      setFilteredThermostatReleases(thermostatReleaseResources)
      setSubmittedSearchValue(searchValue)
      setDisplayFiltered(true)
      setShowSpinner(false)
    }
  }

  const onClickClearIconClicked = () => {
    setSearchValue('')
    setFilteredVideos([])
    setFilteredArticles([])
    setFilteredFaqs([])
    setFilteredThermostatReleases([])
    setSubmittedSearchValue('')
    setDisplayFiltered(false)
    setShowSpinner(false)
  }

  const tabItems = [
    {
      key: 'field-tech-help',
      header: 'Field Tech Help',
      badge: displayFiltered ? fthVideos.length : -1,
      details: <FieldTechHelp searchedValue={submittedSearchValue} videos={fthVideos} isLoading={isFthLoading} />
    },
    {
      key: 'product-literature',
      badge: displayFiltered ? productLiterature.length : -1,
      header: 'Product Literature',
      details: <ProductLiteratureSearch searchedValue={submittedSearchValue} productLiterature={productLiterature} isProductLiteratureLoaded={isProductLiteratureLoaded} serverError={plServerError} />
    },
    {
      key: 'videos',
      header: 'Videos',
      badge: displayFiltered ? filteredVideos.length : -1,
      details: <ResourceTable type="video" searchText={searchValue} resources={displayFiltered ? filteredVideos : videos} isLoaded={areResourcesLoaded} />
    },
    {
      key: 'articles',
      header: 'Articles',
      badge: displayFiltered ? filteredArticles.length : -1,
      details: <ResourceTable type="article" searchText={searchValue} resources={displayFiltered ? filteredArticles : articles} isLoaded={areResourcesLoaded} />
    },
    {
      key: 'faqs',
      header: 'FAQs',
      badge: displayFiltered ? filteredFaqs.length : -1,
      details: <ResourceTable type="faq" searchText={searchValue} resources={displayFiltered ? filteredFaqs : faqs || []} isLoaded={areResourcesLoaded} />
    },
    {
      key: 'thermostat-releases',
      header: <S.Header $displayFiltered={displayFiltered}>Thermostat Releases</S.Header>,
      badge: displayFiltered ? filteredThermostatReleases.length : -1,
      details: <ResourceTable type="thermostat" searchText={searchValue} resources={displayFiltered ? filteredThermostatReleases : thermostatReleases || []} isLoaded={areResourcesLoaded} />
    }
  ]

  const handleTabChange = (index: number) => {
    const tabName = TABS[index] || TABS[0]
    navigate(`/resources/${tabName}`)
  }

  return (
    <S.Container>
      <PageHeader>
        <PageTitle ref={title}>Resources</PageTitle>
        <S.Search
          value={searchValue}
          placeholder="Search"
          ariaLabel="resources search results"
          onChange={setSearchValue}
          onSearch={onSearchClicked}
          onClickClearIcon={onClickClearIconClicked}
          showSpinner={showSpinner}
        />
      </PageHeader>
      <S.SinglePanePage>
        <Tabs
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={handleTabChange}
          tabItems={tabItems}
        />
      </S.SinglePanePage>
    </S.Container>
  )
}

export default Resources

const S = {
  Search: styled(Search)`
    max-width: 500px;

    @media ${screenSize.small} {
      display: block;
      width: 100%;
    }
  `,
  SinglePanePage: styled.div`
    margin: 0px 32px 24px 24px;
    flex: 1;
    min-height: 500px;

    @media ${screenSize.medium} {
      margin: 0px 5px 24px;
    }

    @media ${screenSize.small} {
      padding: 15px;
      margin: 15px;
    }
  `,
  Container: styled.div`
    width: 100%;
  `,
  Header: styled.div<{ $displayFiltered?: boolean }>`
    padding: ${({ $displayFiltered }) => ($displayFiltered ? '0px' : '0px 15px')};
  `
}
